// Import services that handle different aspects of the application
import { AssetService } from "@/services/AssetService";
import {
  getDrawerOptions,
  getSortedContentTypes,
  getSortedMediaItems,
  getSortedTags,
  getVisualBuilderFilteredContentTypes,
  getVisualBuilderFilteredMediaItems,
  getVisualBuilderFilteredTags,
  getVisualBuilderMode,
  isPublicPage,
  transformContentTypesData,
  transformMediaItemData,
  transformTagsData,
} from "@/utils/visualBuilder.js";

import { visualBuilderDataSelector } from "./selectors";
import * as types from "./types";
import { ELEMENT_POSITIONING, EXPERIENCE_PRIMARY_FILTER, SHARE_SCOPE, TAB_STATUSES, VB_MODE } from "../../../constants";
import {
  ExperienceService,
  FlagService,
  PresentationService,
  PublicVisualizationService,
  VisualizationService,
} from "../../../services";
import { setSimpleValue } from "../../simpleValues/actions";

// Initial state for the visual builder
// Contains default values for filters, drawer states, and data collections
const visualBuilderDataInitialState = {
  primaryFilter: EXPERIENCE_PRIMARY_FILTER.TAG,
  typeFilterId: null,
  tagFilterId: null,
  leftDrawer: true,
  rightDrawer: true,
  gridView: false,
  leftDrawerAutoHide: false,
  rightDrawerAutoHide: false,
  tagsToggled: {},
  carousel: false,
  typeTagFilter: false,
  searchField: false,
  currentMediaItemId: null,
  search: {},
  // Arrays to store main data collections
  contentTypes: [],
  filteredContentTypes: [],
  tags: [],
  filteredTags: [],
  mediaItems: [],
  filteredMediaItems: [],
  // Mapping objects for relationships between different data types
  contentTypeTags: {},
  tagContentTypes: {},
  validContentTypes: {},
  validTags: {},
  tagMediaItems: {},
  filteredTagMediaItems: {},
  // State flags and ordering information
  searchPerformed: false,
  contentsOrdered: {},
  experienceContentTypeIds: {},
  experienceTagIds: {},
  contentTypesOrdered: {},
  tagsOrdered: {},
  hiddenContentTypes: {},
  hiddenTags: {},
  locked: false,
  shouldToggleTagItem: true,
  // Asset category related state
  assetCategories: [],
  assetCategoriesOrdered: {},
  assetCategoriesToggled: {},
  assetCategoryContentTypes: {},
  assetCategoryFilterId: null,
  assetCategoryMediaItems: {},
  experienceAssetCategoryIds: {},
  filteredAssetCategories: [],
  filteredAssetCategoryMediaItems: {},
  hiddenAssetCategories: {},
  typesToggled: {},
};

/**
 * Initializes presentation mode with default values
 * Sets initial content type, tag, and media item selections
 * @param {Object} params - Parameters including tab status
 */
const addPresentationInitialValues = (params) => {
  return async (dispatch, getState) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params);
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const { mediaItems, contentTypes, tags, assetCategories } = visualBuilderData;

    // Only proceed if in presentation mode
    if (mode !== VB_MODE.PRESENTATION) {
      return;
    }

    // Initialize values for specific tab statuses when media items exist
    if ([TAB_STATUSES.HOMEBASE, TAB_STATUSES.COLLECT].includes(tabStatus) && mediaItems.length) {
      const initialContentType = contentTypes.find((contentType) => !contentType.hidden);

      const values = { gridView: false };

      // Set initial content type filter
      if (initialContentType) {
        values.typeFilterId = initialContentType.id;
      }

      // Set initial tag filter based on content type
      if (values.typeFilterId) {
        const initialTag = tags.find((tag) => tag.contentTypes.includes(values.typeFilterId) && !tag.hidden);

        if (initialTag) {
          values.tagFilterId = initialTag.id;
        }
      }

      // Set initial asset category
      if (values.typeFilterId) {
        const initialCategory = assetCategories[0];

        if (initialCategory) {
          values.assetCategoryFilterId = initialCategory.id;
        }
      }

      // Set initial media item based on type and tag filters
      if (values.typeFilterId && values.tagFilterId) {
        const initialMediaItem = mediaItems.find(
          (mediaItem) => mediaItem.contentTypeId === values.typeFilterId && mediaItem.tags.includes(values.tagFilterId),
        );

        if (initialMediaItem) {
          values.currentMediaItemId = initialMediaItem.id;
        }
      }

      dispatch(updateVisualBuilderData(params, values, {}, true));
    }
  };
};

/**
 * Initializes visualization mode with default values
 * Sets initial media item selection for customize tab
 * @param {Object} params - Parameters including tab status
 */
const addVisualizationInitialValues = (params) => {
  return async (dispatch, getState) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params);
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const { filteredMediaItems, gridView } = visualBuilderData;

    if (mode !== VB_MODE.VISUALIZATION) {
      return;
    }

    if ([TAB_STATUSES.CUSTOMIZE].includes(tabStatus) && filteredMediaItems.length && !gridView) {
      const values = {};

      // Set initial media item to first filtered item
      const initialMediaItem = filteredMediaItems[0];

      if (initialMediaItem) {
        values.currentMediaItemId = initialMediaItem.id;
      }

      if (Object.keys(values).length) {
        dispatch(updateVisualBuilderData(params, values));
      }
    }
  };
};

/**
 * Initializes public visualization mode with default values
 * Handles different types of public visualizations (normal, media share, single share)
 * @param {Object} params - Parameters including tab status
 */
export const addPublicVisualizationInitialValues = (params) => {
  return async (dispatch, getState) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params);
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const {
      currentMediaItemId,
      filteredMediaItems,
      gridView,
      visibleContentTypes,
      mediaItems,
      scope,
      visibleAssetCategories,
      flags,
    } = visualBuilderData;

    // Check if we're in any public visualization mode
    if (
      ![
        VB_MODE.PUBLIC_VISUALIZATION,
        VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION,
        VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION,
      ].includes(mode)
    ) {
      return;
    }

    // Get drawer options based on current state
    const values = {
      ...getDrawerOptions({ tabStatus, visibleContentTypes, mediaItems, scope, visibleAssetCategories, flags }),
    };

    // Set initial media item for public tab
    if ([TAB_STATUSES.PUBLIC].includes(tabStatus) && filteredMediaItems.length && !gridView && !currentMediaItemId) {
      const initialMediaItem = filteredMediaItems[0];

      if (initialMediaItem) {
        values.currentMediaItemId = initialMediaItem.id;
      }
    }

    if (Object.keys(values).length) {
      dispatch(updateVisualBuilderData(params, values));
    }
  };
};

/**
 * Fetches and initializes all visual builder data
 * Handles different modes (presentation, visualization, public) and their specific data needs
 * @param {Object} params - Parameters including propertyId, experienceId, etc.
 * @param {Object} values - Additional values to set
 */
export const getVisualBuilderData = (params, values = {}) => {
  return async (dispatch, getState) => {
    const { propertyId, experienceId, tabStatus, prevTabStatus, shareId, mediaItemId } = params;
    const { key, mode } = getVisualBuilderMode(params);

    const query = {};
    let initial = false;

    // Set up promises array based on mode
    let promises = [];

    // Fetch data for presentation mode
    if (mode === VB_MODE.PRESENTATION) {
      promises = [
        PresentationService.getContentTypes(propertyId, query),
        PresentationService.getTags(propertyId, query),
        PresentationService.getMediaItems(propertyId, query),
        AssetService.getAssetCategories(propertyId),
        FlagService.getFlags(),
      ];
    }

    // Fetch data for visualization mode
    if (mode === VB_MODE.VISUALIZATION) {
      promises = [
        VisualizationService.getContentTypes(propertyId, experienceId, query),
        VisualizationService.getTags(propertyId, experienceId, query),
        VisualizationService.getMediaItems(propertyId, experienceId, query),
        AssetService.getAssetCategories(propertyId),
        FlagService.getFlags(),
      ];
    }

    // Fetch data for public visualization modes
    if (
      [
        VB_MODE.PUBLIC_VISUALIZATION,
        VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION,
        VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION,
      ].includes(mode)
    ) {
      promises = [
        PublicVisualizationService.getContentTypes(propertyId, experienceId, query, mediaItemId),
        PublicVisualizationService.getTags(propertyId, experienceId, query, mediaItemId),
        PublicVisualizationService.getMediaItems(propertyId, experienceId, query, mediaItemId),
        AssetService.getAssetCategories(propertyId),
        PublicVisualizationService.getFlags(),
      ];
    }

    // Wait for all data to be fetched
    const [
      {
        resp: { data: contentTypes },
      },
      {
        resp: { data: tags },
      },
      {
        resp: { data: mediaItems },
      },
      {
        resp: { data: assetCategories },
      },
      {
        resp: { data: flags },
      },
    ] = await Promise.all(promises);

    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    // Prepare payload with initial state if needed
    let payload = {
      ...(!visualBuilderData.id && { ...visualBuilderDataInitialState }),
      key,
      mode,
      tabStatus,
      ...visualBuilderData,
      ...values,
    };

    // Handle tab changes
    if (tabStatus === TAB_STATUSES.CUSTOMIZE && prevTabStatus === TAB_STATUSES.COLLECT && payload.tabChanged) {
      const { visualBuilderData } = visualBuilderDataSelector({ ...params, tabStatus: TAB_STATUSES.COLLECT })(
        getState(),
      );

      payload.gridView = visualBuilderData.gridView;
    }

    // Transform flags array into object
    payload.flags = flags.reduce((prev, val) => ({ ...prev, [val.name]: val.value }), {});
    payload.contentTypes = contentTypes;
    payload.tags = tags;
    payload.mediaItems = mediaItems;
    payload.assetCategories = assetCategories;

    // Handle public media share visualization
    if ([VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION, VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION].includes(mode)) {
      const { resp: share } = await PublicVisualizationService.getPublicMediaShare(propertyId, shareId);

      if (share && share.options) {
        // Apply share options to payload
        if (share.options.scope !== undefined) payload.scope = share.options.scope;
        if (share.options.currentMediaItemId !== undefined)
          payload.currentMediaItemId = share.options.currentMediaItemId;
        if (share.options.primaryFilter !== undefined) payload.primaryFilter = share.options.primaryFilter;
        if (share.options.typeFilterId !== undefined) payload.typeFilterId = share.options.typeFilterId;
        if (share.options.tagFilterId !== undefined) payload.tagFilterId = share.options.tagFilterId;
        if (share.createdBy !== undefined) payload.createdBy = share.createdBy;
        if (share.id !== undefined) payload.currentShareId = share.id;
      }
    }

    // Ensure other tag is included
    if (!payload.tags.find((tag) => tag.id === payload.otherTag.id)) {
      payload.tags.push(payload.otherTag);
    }

    // Handle public page with media item scope
    if (
      isPublicPage(payload.tabStatus) &&
      payload.scope &&
      payload.scope === SHARE_SCOPE.MEDIA_ITEM &&
      payload.mediaItems.length
    ) {
      let initialMediaItem;

      if (payload.currentMediaItemId) {
        initialMediaItem = payload.mediaItems.find((mediaItem) => mediaItem.id === payload.currentMediaItemId);
      }

      if (!initialMediaItem) {
        initialMediaItem = payload.mediaItems[0];
      }

      payload.gridView = false;

      if (initialMediaItem) {
        payload.mediaItems = [initialMediaItem];
      }
    }

    // Transform and sort data
    payload = transformContentTypesData(payload);
    payload = transformTagsData(payload);
    payload = transformMediaItemData(payload);
    payload = getSortedContentTypes(payload);
    payload = getSortedTags(payload);
    payload = getSortedMediaItems(payload);

    // Apply filters
    payload = getVisualBuilderFilteredMediaItems(payload);
    payload = getVisualBuilderFilteredContentTypes(payload);
    payload = getVisualBuilderFilteredTags(payload);

    // Initialize if needed
    if (!payload.id) {
      payload.id = crypto.randomUUID();
      payload.initialized = true;
      initial = true;
    }

    // Handle single menu redesign flag
    if (payload.flags?.["single-menu-redesign"]) {
      payload.rightDrawer = false;
      payload.rightDrawerAutoHide = true;
    }

    // Dispatch action to update state
    dispatch({
      type: types.GET_VISUAL_BUILDER_DATA,
      payload,
    });

    // Add initial values for different modes
    if (initial) {
      if (mode === VB_MODE.PRESENTATION) {
        dispatch(addPresentationInitialValues(params));
      }

      if (mode === VB_MODE.VISUALIZATION) {
        dispatch(addVisualizationInitialValues(params));
      }
    }

    if (
      [
        VB_MODE.PUBLIC_VISUALIZATION,
        VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION,
        VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION,
      ].includes(mode)
    ) {
      dispatch(addPublicVisualizationInitialValues(params));
    }
  };
};
/**
 * Refreshes the visual builder data without fetching from server
 * Re-sorts and re-filters existing data
 * @param {Object} params - Parameters for the refresh operation
 */
export const refreshVisualBuilderData = (params) => {
  return async (dispatch, getState) => {
    const { key, mode } = getVisualBuilderMode(params);
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    let payload = {
      key,
      mode,
      ...visualBuilderData,
    };

    // Re-apply sorting and filtering
    payload = getSortedContentTypes(payload);
    payload = getSortedTags(payload);
    payload = getSortedMediaItems(payload);

    payload = getVisualBuilderFilteredMediaItems(payload);
    payload = getVisualBuilderFilteredContentTypes(payload);
    payload = getVisualBuilderFilteredTags(payload);

    // Update state with refreshed data
    dispatch({
      type: types.UPDATE_VISUAL_BUILDER_DATA,
      payload: { key, ...payload },
    });

    // Handle public visualization modes
    if (
      [
        VB_MODE.PUBLIC_VISUALIZATION,
        VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION,
        VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION,
      ].includes(mode)
    ) {
      dispatch(addPublicVisualizationInitialValues(params));
    }
  };
};

/**
 * Updates visual builder data and handles side effects
 * @param {Object} params - Parameters for the update
 * @param {Object} values - New values to update
 * @param {Object} options - Additional options for the update
 * @param {boolean} refresh - Whether to refresh data after update
 */
export const updateVisualBuilderData = (params, values, options = {}, refresh = false) => {
  return async (dispatch, getState) => {
    const { key, mode } = getVisualBuilderMode(params);

    // Update visual builder state
    dispatch({
      type: types.UPDATE_VISUAL_BUILDER_DATA,
      payload: { key, ...values, ...options },
    });

    // For visualization mode, sync changes with server
    if (mode === VB_MODE.VISUALIZATION && Object.keys(values).length) {
      await ExperienceService.updateExperience(params.propertyId, params.experienceId, values);
    }

    // Handle voice memo playback state
    const voiceMemoPlayback = getState().simpleValues?.["voice-memo-playback"];

    // Close voice memo playback if switching content while memo is playing
    if (
      values.currentMediaItemId &&
      voiceMemoPlayback?.element?.positioning === ELEMENT_POSITIONING.CONTENT &&
      voiceMemoPlayback?.element?.mediaItemId !== values.currentMediaItemId &&
      voiceMemoPlayback?.open
    ) {
      dispatch(setSimpleValue("voice-memo-playback", { open: false }));
    }

    // Refresh data if requested
    if (refresh) {
      dispatch(refreshVisualBuilderData(params));
    }
  };
};

/**
 * Resets visual builder data to initial state
 * @param {Object} params - Parameters for the reset operation
 */
export const resetVisualBuilderData = (params) => {
  return async (dispatch) => {
    const { key } = getVisualBuilderMode(params);

    // Dispatch reset action with key
    dispatch({
      type: types.RESET_VISUAL_BUILDER_DATA,
      payload: { key },
    });
  };
};
